import Link from 'next/link';
import { useRouter } from 'next/router';
import {
  Children,
  FC,
  HTMLProps,
  ReactNode,
  forwardRef,
  useEffect,
} from 'react';
import dynamic from 'next/dynamic';

import spinner from '@public/img/partnerships/spinner.json';

const Lottie = dynamic(() => import('lottie-react'), { ssr: false });

const bottomButtonStyles =
  'flex min-w-full cursor-pointer items-center justify-center rounded-full bg-blue py-2 px-3.5 font-semibold tracking-tight text-mono-white hover:bg-blue-800 active:bg-blue-800';

export const NavigationButton: FC<
  { children: ReactNode; href: string } & HTMLProps<HTMLAnchorElement>
> = ({
  children,
  href,
  ...props
}: {
  children: ReactNode;
  href: string;
}): JSX.Element => (
  <Link href={href} className={bottomButtonStyles} {...props}>
    {children}
  </Link>
);

export const NavigationButtonWrapper = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => <div className="mt-8 pb-8">{children}</div>;

export const SubmitButton: FC<
  {
    children: ReactNode;
    isLoading: boolean;
    prefetchHref: string;
  } & HTMLProps<HTMLButtonElement>
> = ({
  children,
  isLoading,
  prefetchHref,
  ...props
}: {
  children: ReactNode;
  isLoading: boolean;
  prefetchHref: string;
}): JSX.Element => {
  const { prefetch } = useRouter();

  useEffect(() => {
    if (prefetchHref) {
      prefetch(prefetchHref);
    }
  }, [prefetchHref, prefetch]);

  return (
    <button className={bottomButtonStyles} disabled={isLoading} {...props}>
      {isLoading ? (
        <Lottie
          animationData={spinner}
          style={{
            maxWidth: 36,
            minWidth: 36,
          }}
        />
      ) : (
        children
      )}
    </button>
  );
};

export const QuestionButton: FC<
  { children: ReactNode } & HTMLProps<HTMLButtonElement>
> = ({ children, ...props }: { children: ReactNode }): JSX.Element => (
  <button
    {...props}
    className="inline-flex min-w-full flex-1 items-center justify-center rounded-md border border-[rgba(0,0,0,0)] bg-dawn-blue-600 py-3 text-base font-medium text-mono-white shadow-sm hover:bg-dawn-blue-800 focus:outline-none focus:ring-2 focus:ring-dawn-blue-400 focus:ring-offset-2"
  >
    {children}
  </button>
);

export const ResponsiveButtonPair = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => (
  <div className="grid grid-cols-12 gap-6">
    {Children.map(children, (child, index) => (
      <div key={index} className="col-span-12 md:col-span-6">
        {child}
      </div>
    ))}
  </div>
);

export const DepthEffectButton = forwardRef<
  HTMLButtonElement,
  { children: ReactNode } & HTMLProps<HTMLButtonElement>
>(({ children, ...props }: { children: ReactNode }, ref) => (
  <button
    {...props}
    className="relative flex min-h-[44px] min-w-full cursor-pointer items-center justify-center rounded-full bg-blue-800 font-cera tracking-wider text-mono-white shadow-custom-button-default transition duration-100 active:translate-y-[2px] active:shadow-none disabled:opacity-50"
    ref={ref}
  >
    {children}
  </button>
));

export const DepthEffectQuestionButton = forwardRef<
  HTMLButtonElement,
  { children: ReactNode } & HTMLProps<HTMLButtonElement>
>(({ children, ...props }: { children: ReactNode }, ref) => (
  <button
    {...props}
    ref={ref}
    className="inline-flex min-w-full flex-1 items-center justify-center rounded-md border-[rgba(0,0,0,0)] bg-dawn-blue-200 px-2 py-3 font-cera text-base text-blue shadow-custom-question-button-default transition duration-100 hover:bg-dawn-blue-200 focus:outline-none focus:ring-dawn-blue-100 focus:ring-offset-2 active:translate-y-[2px] active:shadow-none disabled:opacity-50"
  >
    {children}
  </button>
));

export const DepthEffectQuestionButtonSelected = forwardRef<
  HTMLButtonElement,
  { children: ReactNode } & HTMLProps<HTMLButtonElement>
>(({ children, ...props }: { children: ReactNode }, ref) => (
  <button
    {...props}
    ref={ref}
    className="inline-flex min-w-full flex-1 items-center justify-center rounded-md border-[rgba(0,0,0,0)] bg-dawn-blue-600 px-2 py-3 font-cera text-base text-blue shadow-custom-question-button-selected transition duration-100 hover:bg-dawn-blue-600 focus:outline-none focus:ring-purple-400 focus:ring-offset-2 active:translate-y-[2px] active:shadow-none disabled:opacity-50"
  >
    {children}
  </button>
));

export const LoadingButton = ({
  isSubmitting,
  children,
}: {
  isSubmitting: boolean;
  children: ReactNode;
}): JSX.Element =>
  isSubmitting ? (
    <Lottie
      animationData={spinner}
      style={{
        maxWidth: 60,
        minWidth: 60,
      }}
      loop
    />
  ) : (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>{children}</>
  );
